import { useState } from "react";

import imgUrl from "@/assets/images/logo.svg";
import useLogin from "@/hooks/network/useLogin";

interface LoginProps {
  onLogin: ({
    accessToken,
    idToken,
  }: {
    accessToken: string;
    idToken: string;
  }) => void;
}

const Login = ({ onLogin }: LoginProps) => {
  const [hasError, setHasError] = useState(false);
  const [username, setUsername] = useState<string | null>(null);
  const [password, setPassword] = useState<string | null>(null);
  const { mutate: login, isPending: isAuthenticating } = useLogin({
    onError: (error) => {
      setHasError(true);
      throw new Error(`Login Failed: ${error.message}`);
    },
    onSuccess: ({ data }) => {
      const { access_token: accessToken, id_token: idToken } = data;
      onLogin({ accessToken, idToken });
    },
  });

  const handleLogin = async () => {
    login({ username: username!, password: password! });
  };

  return (
    <>
      <div className="min-h-full flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <img className="mx-auto h-12 w-auto" src={imgUrl} alt="Workflow" />
            {/*<h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">*/}
            {/*  Sign in*/}
            {/*</h2>*/}
          </div>
          {hasError && (
            <div className="w-full text-center mt-8 space-y-6">
              <div className="text-lg text-red-600">Login Failed!</div>
            </div>
          )}
          <div className="mt-8 space-y-6">
            <input type="hidden" name="remember" defaultValue="true" />
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="email-address" className="sr-only">
                  Email address
                </label>
                <input
                  id="email-address"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  onChange={(e) => setUsername(e.target.value)}
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Email address"
                />
              </div>
              <div>
                <label htmlFor="password" className="sr-only">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  // onBlur={(e) => setPassword(e.target.value)}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleLogin();
                    }
                  }}
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Password"
                />
              </div>
            </div>
            {/*<div className="flex items-center justify-between">*/}
            {/*  <div className="text-sm">*/}
            {/*    <button*/}
            {/*      onClick={() => {}}*/}
            {/*      className="font-medium text-blue-600 hover:text-blue-500"*/}
            {/*    >*/}
            {/*      Forgot your password?*/}
            {/*    </button>*/}
            {/*  </div>*/}
            {/*</div>*/}
            <div>
              {isAuthenticating ? (
                <button
                  disabled
                  type="button"
                  className="group relative w-full flex justify-center text-white bg-blue-500 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center mr-2 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800 inline-flex items-center"
                >
                  <svg
                    role="status"
                    className="inline w-4 h-4 mr-3 text-white animate-spin"
                    viewBox="0 0 100 101"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                      fill="#E5E7EB"
                    />
                    <path
                      d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                      fill="currentColor"
                    />
                  </svg>
                  Authenticating...
                </button>
              ) : (
                <button
                  type="submit"
                  className="group relative w-full flex justify-center text-white py-2 px-4 border border-transparent text-sm font-medium rounded-md bg-blue-500 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2"
                  onClick={handleLogin}
                >
                  Sign in
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
